import LocationDialog from "@src/features/locations/dialogs/LocationDialog";
import { HrisLocationFragment } from "@src/features/settings/hris/HrisLocationListContainer.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { useTracking } from "@src/features/app/hooks/useTracking";
import useLocations from "@src/features/locations/hooks/useLocations";
import { GraphqlOperations } from "@src/types.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Label } from "@src/lib/ui/Label";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import PinOutlineIcon from "@src/lib/icons/18px/pin-outline";
import PlusIcon from "@src/lib/icons/18px/plus";
import Fuse from "fuse.js";
import { FC, useMemo, useState, useRef } from "react";
import { gql } from "@apollo/client";
import { useLinkHrisLocationToOpusLocationMutation } from "./LinkHrisLocationDialog.generated";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";

const fuseConfig = {
  threshold: 0.4,
  keys: ["name"],
};

const CREATE_NEW_VALUE = "__create_new__";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hrisLocation: HrisLocationFragment;
};

const LinkHrisLocationDialog: FC<Props> = ({
  open,
  onOpenChange,
  hrisLocation,
}) => {
  const [createLocationOpen, setCreateLocationOpen] = useState(false);
  const { addErrorToast } = useToast();
  const { trackEvent } = useTracking();
  const [search, setSearch] = useState("");
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const { locations } = useLocations();
  const [selectedValue, setSelectedValue] = useState<string>(
    hrisLocation.group?.id.toString() || "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const initialValueRef = useRef<string>(
    hrisLocation.group?.id.toString() || "",
  );

  const hasChanged = useMemo(() => {
    return selectedValue !== initialValueRef.current;
  }, [selectedValue]);

  const filteredLocations = useMemo(() => {
    if (search) {
      return new Fuse(locations, fuseConfig).search(search).map((e) => e.item);
    }
    return [...locations].sort((a, b) => {
      if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }, [locations, search]);

  const selectedLocation = useMemo(() => {
    if (selectedValue === CREATE_NEW_VALUE) {
      return {
        id: CREATE_NEW_VALUE,
        name: `Create new Opus location for ${hrisLocation.name}`,
      };
    }
    if (!selectedValue) return null;
    return (
      locations.find((loc) => loc.id.toString() === selectedValue) ||
      (hrisLocation.group?.id.toString() === selectedValue
        ? {
            id: hrisLocation.group.id,
            name: hrisLocation.group.name,
          }
        : null)
    );
  }, [selectedValue, locations, hrisLocation.group, hrisLocation.name]);

  const [createHrisLocationLink] = useLinkHrisLocationToOpusLocationMutation({
    refetchQueries: [
      GraphqlOperations.Query.HrisLocations,
      GraphqlOperations.Query.HrisLocationSidebar,
    ],
    onCompleted: (data) => {
      setIsSaving(false);
      if (!data.createHrisLocationLink.success) {
        addErrorToast({
          hrisLocationId: hrisLocation.id,
          opusLocationId:
            selectedValue !== CREATE_NEW_VALUE
              ? parseInt(selectedValue)
              : undefined,
          callsite: "new_hris_location_link",
        });
      } else {
        trackEvent("new_hris_location_link", {
          hrisLocationId: hrisLocation.id,
          opusLocationId:
            selectedValue !== CREATE_NEW_VALUE
              ? parseInt(selectedValue)
              : undefined,
        });
        onOpenChange(false);
        launchConfetti();
      }
    },
    onError: (error) => {
      setIsSaving(false);
      addErrorToast({
        ...error,
        callsite: "new_group_dialog",
      });
    },
  });

  const handleSave = () => {
    if (!selectedValue) return;

    setIsSaving(true);
    createHrisLocationLink({
      variables: {
        hrisLocationId: hrisLocation.id,
        groupId: parseInt(selectedValue),
      },
    });
  };

  const handleSelect = (value: string) => {
    if (value === CREATE_NEW_VALUE) {
      setComboboxOpen(false);
      setSearch("");
      setCreateLocationOpen(true);
    } else {
      setSelectedValue(value);
      setComboboxOpen(false);
      setSearch("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit opus location link</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Opus location</Label>
          <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className="h-9 w-full justify-between font-normal"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {(selectedLocation ||
                    (selectedValue && hrisLocation.group)) &&
                    selectedLocation?.id !== CREATE_NEW_VALUE && (
                      <PinOutlineIcon className="size-4 shrink-0 text-muted-foreground" />
                    )}
                  <span className="truncate">
                    {selectedLocation
                      ? selectedLocation.name
                      : selectedValue && hrisLocation.group?.name
                        ? hrisLocation.group.name
                        : "Select a location..."}
                  </span>
                </div>
                <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
              disablePortal
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search for a location..."
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>
                    {search ? "No locations found" : "No locations available"}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value={CREATE_NEW_VALUE}
                      onSelect={handleSelect}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-1 items-center gap-2">
                        <PlusIcon
                          className={cn(
                            "h-4 w-4 shrink-0",
                            selectedValue === CREATE_NEW_VALUE
                              ? "text-primary"
                              : "text-muted-foreground",
                          )}
                        />
                        <Text
                          variant="compact-description"
                          className={cn(
                            selectedValue === CREATE_NEW_VALUE &&
                              "text-primary",
                          )}
                        >
                          Create new Opus location for {hrisLocation.name}
                        </Text>
                      </div>
                    </CommandItem>

                    {filteredLocations.map((location) => {
                      const value = location.id.toString();
                      const isSelected = selectedValue === value;
                      return (
                        <CommandItem
                          key={location.id}
                          value={value}
                          onSelect={handleSelect}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-1 items-center gap-2">
                            <PinOutlineIcon
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isSelected
                                  ? "text-primary"
                                  : "text-muted-foreground",
                              )}
                            />
                            <Text
                              variant="compact-description"
                              className={cn(isSelected && "text-primary")}
                            >
                              {location.name}
                            </Text>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedValue || isSaving || !hasChanged}
            loading={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {createLocationOpen && (
        <LocationDialog
          open={createLocationOpen}
          onOpenChange={setCreateLocationOpen}
          initialInput={{ name: hrisLocation.name }}
          navigateToLocationOnSave={false}
          onLocationCreated={(locationId) => {
            setSelectedValue(locationId.toString());
          }}
        />
      )}
    </Dialog>
  );
};

export default LinkHrisLocationDialog;

gql`
  mutation LinkHrisLocationToOpusLocation(
    $groupId: Int!
    $hrisLocationId: Int!
  ) {
    createHrisLocationLink(groupId: $groupId, hrisLocationId: $hrisLocationId) {
      success
    }
  }
`;
